import {ApiClient, ApiClientConfig} from './api-client/api-client';
import {AppConfigurationEndpoint} from './endpoints/app-configuration';
import {AudiencesEndpoint} from './endpoints/audiences';
import {BlocksEndpoint} from './endpoints/blocks';
import {BrandsEndpoint} from './endpoints/brands';
import {BuyersEndpoint} from './endpoints/buyers';
import {CampaignsEndpoint} from './endpoints/campaigns';
import {CompaniesEndpoint} from './endpoints/companies';
import {CrmIntegrationsEndpoint} from './endpoints/crm-integrations';
import {CrmSyncEndpoint} from './endpoints/crm-sync';
import {CustomCodesEndpoint} from './endpoints/custom-codes';
import {CustomerPortalEndpoint} from './endpoints/customer-portal';
import {CustomersEndpoint} from './endpoints/customers';
import {IpAllocationImportsEndpoint} from './endpoints/ip-allocation-imports';
import {JobsEndpoint} from './endpoints/jobs';
import {LandingPageDomainEndpoint} from './endpoints/landing-page-domain';
import {LandingPagesEndpoint} from './endpoints/landing-pages';
import {LeadFormsEndpoint} from './endpoints/lead-forms';
import {LeadsEndpoint} from './endpoints/leads';
import {OrganizationsEndpoint} from './endpoints/organizations';
import {OrganizationMembersEndpoint} from './endpoints/organization-members';
import {OutreachDemosEndpoint} from './endpoints/outreach-demos';
import {PricesEndpoint} from './endpoints/prices';
import {ProductGroupsEndpoint} from './endpoints/product-groups';
import {QuotasEndpoint} from './endpoints/quotas';
import {QuotaTransactionsEndpoint} from './endpoints/quota-transactions';
import {ProductsEndpoint} from './endpoints/products';
import {QaCampaignsEndpoint} from './endpoints/qa-campaigns';
import {ScimEndpoint} from './endpoints/scim';
import {SegmentActionsEndpoint} from './endpoints/segment-actions';
import {ServiceAccountsEndpoint} from './endpoints/service-accounts';
import {SsoCertificatesEndpoint} from './endpoints/sso-certificates';
import {SsoConnectionsEndpoint} from './endpoints/sso-connections';
import {SsoDomainsEndpoint} from './endpoints/sso-domains';
import {SubscriptionsEndpoint} from './endpoints/subscriptions';
import {PagesEndpoint} from './endpoints/pages';
import {UsersEndpoint} from './endpoints/users';

class TinctApi {
  private readonly client: ApiClient;
  public readonly appConfiguration: AppConfigurationEndpoint;
  public readonly audiences: AudiencesEndpoint;
  public readonly blocks: BlocksEndpoint;
  public readonly brands: BrandsEndpoint;
  public readonly buyers: BuyersEndpoint;
  public readonly campaigns: CampaignsEndpoint;
  public readonly companies: CompaniesEndpoint;
  public readonly crmIntegrations: CrmIntegrationsEndpoint;
  public readonly crmSync: CrmSyncEndpoint;
  public readonly customCodes: CustomCodesEndpoint;
  public readonly customerPortal: CustomerPortalEndpoint;
  public readonly customers: CustomersEndpoint;
  public readonly ipAllocationImports: IpAllocationImportsEndpoint;
  public readonly jobs: JobsEndpoint;
  public readonly landingPageDomain: LandingPageDomainEndpoint;
  public readonly landingPages: LandingPagesEndpoint;
  public readonly leadForms: LeadFormsEndpoint;
  public readonly leads: LeadsEndpoint;
  public readonly pages: PagesEndpoint;
  public readonly organizations: OrganizationsEndpoint;
  public readonly organizationMembers: OrganizationMembersEndpoint;
  public readonly outreachDemos: OutreachDemosEndpoint;
  public readonly prices: PricesEndpoint;
  public readonly productGroups: ProductGroupsEndpoint;
  public readonly products: ProductsEndpoint;
  public readonly qaCampaigns: QaCampaignsEndpoint;
  public readonly quotas: QuotasEndpoint;
  public readonly quotaTransactions: QuotaTransactionsEndpoint;
  public readonly scim: ScimEndpoint;
  public readonly segmentActions: SegmentActionsEndpoint;
  public readonly serviceAccounts: ServiceAccountsEndpoint;
  public readonly ssoCertificates: SsoCertificatesEndpoint;
  public readonly ssoConnections: SsoConnectionsEndpoint;
  public readonly ssoDomains: SsoDomainsEndpoint;
  public readonly subscriptions: SubscriptionsEndpoint;
  public readonly users: UsersEndpoint;

  constructor(config: ApiClientConfig) {
    this.client = new ApiClient(config);
    this.appConfiguration = new AppConfigurationEndpoint(this.client);
    this.audiences = new AudiencesEndpoint(this.client);
    this.blocks = new BlocksEndpoint(this.client);
    this.brands = new BrandsEndpoint(this.client);
    this.buyers = new BuyersEndpoint(this.client);
    this.campaigns = new CampaignsEndpoint(this.client);
    this.companies = new CompaniesEndpoint(this.client);
    this.crmIntegrations = new CrmIntegrationsEndpoint(this.client);
    this.crmSync = new CrmSyncEndpoint(this.client);
    this.customCodes = new CustomCodesEndpoint(this.client);
    this.customerPortal = new CustomerPortalEndpoint(this.client);
    this.customers = new CustomersEndpoint(this.client);
    this.ipAllocationImports = new IpAllocationImportsEndpoint(this.client);
    this.jobs = new JobsEndpoint(this.client);
    this.landingPageDomain = new LandingPageDomainEndpoint(this.client);
    this.landingPages = new LandingPagesEndpoint(this.client);
    this.leadForms = new LeadFormsEndpoint(this.client);
    this.leads = new LeadsEndpoint(this.client);
    this.pages = new PagesEndpoint(this.client);
    this.organizations = new OrganizationsEndpoint(this.client);
    this.organizationMembers = new OrganizationMembersEndpoint(this.client);
    this.outreachDemos = new OutreachDemosEndpoint(this.client);
    this.prices = new PricesEndpoint(this.client);
    this.productGroups = new ProductGroupsEndpoint(this.client);
    this.products = new ProductsEndpoint(this.client);
    this.qaCampaigns = new QaCampaignsEndpoint(this.client);
    this.quotas = new QuotasEndpoint(this.client);
    this.quotaTransactions = new QuotaTransactionsEndpoint(this.client);
    this.scim = new ScimEndpoint(this.client);
    this.segmentActions = new SegmentActionsEndpoint(this.client);
    this.serviceAccounts = new ServiceAccountsEndpoint(this.client);
    this.ssoCertificates = new SsoCertificatesEndpoint(this.client);
    this.ssoConnections = new SsoConnectionsEndpoint(this.client);
    this.ssoDomains = new SsoDomainsEndpoint(this.client);
    this.subscriptions = new SubscriptionsEndpoint(this.client);
    this.users = new UsersEndpoint(this.client);
  }
}

export type { ApiClientConfig } from './api-client/api-client';
export { ApiError } from './api-client/api-error';
export * from './endpoints/users';

export const tinctApi = new TinctApi({
  baseUrl: `${import.meta.env['VITE_API_URL']}` || 'https://localhost:8080',
});
